import { useEffect, useMemo, useState } from "react";

import { getContactPhone } from "src/env";
import { joinInvite } from "src/api/client";
import type { JoinedQueueEntry } from "src/api/types";
import { useInvite } from "src/hooks/useInvite";
import { useWaitingSSE } from "src/hooks/useWaitingSSE";
import { WaitingScreen } from "src/components/WaitingScreen";

const RETRY_KEY_PREFIX = "walkin:invite_retry_key:";

interface Props {
  shortCode: string;
}

function loadOrCreateRetryKey(shortCode: string): string {
  const key = RETRY_KEY_PREFIX + shortCode;
  const stored = localStorage.getItem(key);
  if (stored !== null) return stored;
  const retryKey = crypto.randomUUID();
  localStorage.setItem(key, retryKey);
  return retryKey;
}

export function InviteLanding({ shortCode }: Props) {
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joined, setJoined] = useState<JoinedQueueEntry | null>(null);
  const [queueJustClosed, setQueueJustClosed] = useState(false);
  // Once joined, the pre-join queue_open poll no longer serves any purpose -- useWaitingSSE
  // takes over keeping the patient up to date.
  const { data, loading, error, refetch } = useInvite(shortCode, !joined);
  const retryKey = useMemo(() => loadOrCreateRetryKey(shortCode), [shortCode]);

  const waitingState = useWaitingSSE(joined?.client_id ?? "", joined?.stream_token ?? "");

  // A fresh poll reporting queue_open re-enables the button; joining itself doesn't clear
  // this, since a stale queue_open is exactly what let the join 409 in the first place.
  useEffect(() => {
    // A fresh poll is the signal to re-enable the button.
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    if (data?.queue_open) setQueueJustClosed(false);
  }, [data]);

  if (joined) {
    return (
      <WaitingScreen
        clientId={joined.client_id}
        streamToken={joined.stream_token}
        state={waitingState}
      />
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="queue-body">
          <div className="queue-container">
            <div className="queue-card">
              <p>Loading…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error === "invite_not_found") {
    return (
      <div className="dashboard">
        <div className="queue-body">
          <div className="queue-container">
            <div className="queue-card">
              <p>
                This link isn't valid. Please check the link or contact us at {getContactPhone()}.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="queue-body">
          <div className="queue-container">
            <div className="queue-card">
              <p>
                We couldn't load your invite. Please refresh this page or contact us at{" "}
                {getContactPhone()}.
              </p>
              <button className="btn btn-primary btn-full" onClick={refetch}>
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function handleJoin() {
    setJoining(true);
    setJoinError(null);
    try {
      const entry = await joinInvite(shortCode, retryKey);
      setJoined(entry);
    } catch (err) {
      const message = (err as Error).message;
      if (message === "queue_closed") {
        setJoinError("queue_closed");
        setQueueJustClosed(true);
      } else if (message === "invite_not_found") {
        setJoinError("invite_not_found");
      } else {
        setJoinError(message);
      }
    }
    setJoining(false);
  }

  if (joinError === "invite_not_found") {
    return (
      <div className="dashboard">
        <div className="queue-body">
          <div className="queue-container">
            <div className="queue-card">
              <p>
                This link isn't valid. Please check the link or contact us at {getContactPhone()}.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const queueClosed = data?.queue_open === false || queueJustClosed;

  return (
    <div className="dashboard">
      <div className="queue-body">
        <div className="queue-container">
          <div className="queue-card">
            <h1>
              {data?.patient_first_name
                ? `Hi ${data.patient_first_name}, ready to see a nurse today?`
                : "Ready to see a nurse today?"}
            </h1>
            <p>
              Tap the button below when you're ready. We'll hold your spot and keep you updated on
              this page — no need to refresh.
            </p>
            <button
              className="btn btn-primary btn-full"
              disabled={queueClosed || joining}
              onClick={() => void handleJoin()}
            >
              {joining ? "Joining…" : "Join the queue"}
            </button>
            {queueClosed && (
              <p className="queue-closed-note">
                {queueJustClosed
                  ? "The queue just closed. We'll re-enable this if it reopens."
                  : `The queue is closed right now. Call us at ${getContactPhone()} for help.`}
              </p>
            )}
            {joinError && joinError !== "queue_closed" && joinError !== "invite_not_found" && (
              <p className="modal-error">We couldn't join the queue. Please try again.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
